import { useEffect, useRef } from "react";
import { PDFDocument } from "pdf-lib";

import expensesService from '../../services/expenses';

const usePrevious = (value) => {
    const ref = useRef();
    
    useEffect(() => {
        ref.current = value; //assign the value of ref to the argument
    },[value]); //this code will run when the value of 'value' changes
    
    return ref.current; //in the end, return the current ref value.
}

const currencyFormat = (value) => {
  // 1) Trata null, undefined, vazio e string "null"
  if (value === null || value === undefined || value === '' || value === 'null') {
    return '';
  }

  let num;

  // 2) Se já for número, usa direto
  if (typeof value === 'number') {
    num = value;
  } else {
    // 3) Se vier como string, limpa possíveis "R$", pontos e vírgula
    const cleaned = String(value)
      .replace('R$', '')
      .replace(/\./g, '')
      .replace(',', '.')
      .trim();

    num = Number(cleaned);
  }

  // 4) Se ainda assim não der número, retorna vazio
  if (Number.isNaN(num)) {
    return '';
  }

  // 5) Formata em moeda BRL
  return num.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};


const BLACKLIST = ['00000000000000','11111111111111','22222222222222','33333333333333','44444444444444','55555555555555','66666666666666','77777777777777','88888888888888','99999999999999','12345678909876'];

const isValidCNPJ = (cnpj) => {
    // Remove non-digits
    const cleanCNPJ = cnpj.replace(/[^\d]/g, '');

    // Check against the blacklist
    if (BLACKLIST.includes(cleanCNPJ)) {
        return false; // CNPJ is in the blacklist
    }

    // Your other CNPJ validation logic here
    // ...

    return /^\d{14}$/.test(cleanCNPJ); // Basic length check
};

// Valida o CNPJ conferindo os dígitos verificadores (algoritmo módulo 11 da Receita Federal)
const isValidCNPJDigits = (cnpj) => {
    const clean = cnpj.replace(/[^\d]/g, '');

    if (clean.length !== 14 || BLACKLIST.includes(clean) || /^(\d)\1{13}$/.test(clean)) {
        return false;
    }

    const calcDigit = (base) => {
        let sum = 0;
        let pos = base.length - 7;
        for (let i = base.length; i >= 1; i--) {
            sum += Number(base.charAt(base.length - i)) * pos--;
            if (pos < 2) pos = 9;
        }
        const result = sum % 11;
        return result < 2 ? 0 : 11 - result;
    };

    const firstDigit = calcDigit(clean.substring(0, 12));
    if (firstDigit !== Number(clean.charAt(12))) return false;

    const secondDigit = calcDigit(clean.substring(0, 13));
    if (secondDigit !== Number(clean.charAt(13))) return false;

    return true;
};

// Valida o CPF conferindo os dígitos verificadores (algoritmo módulo 11 da Receita Federal)
const isValidCPF = (cpf) => {
    const clean = cpf.replace(/[^\d]/g, '');

    if (clean.length !== 11 || /^(\d)\1{10}$/.test(clean)) {
        return false;
    }

    const calcDigit = (base) => {
        let sum = 0;
        for (let i = 0; i < base.length; i++) {
            sum += Number(base.charAt(i)) * (base.length + 1 - i);
        }
        const result = (sum * 10) % 11;
        return result === 10 ? 0 : result;
    };

    const firstDigit = calcDigit(clean.substring(0, 9));
    if (firstDigit !== Number(clean.charAt(9))) return false;

    const secondDigit = calcDigit(clean.substring(0, 10));
    if (secondDigit !== Number(clean.charAt(10))) return false;

    return true;
};

// Detecta pelo tamanho se o valor é um CPF (11 dígitos) ou CNPJ (14 dígitos) e valida os dígitos verificadores
const isValidCnpjCpf = (value) => {
    const clean = (value || '').replace(/[^\d]/g, '');

    if (clean.length === 11) return isValidCPF(clean);
    if (clean.length === 14) return isValidCNPJDigits(clean);

    return false;
};

const isValidDate = (date) => {
    const nd = new Date(date);
    if(nd instanceof Date && !isNaN(nd.valueOf())){
        return true;
    }

    return false;
};

const returnDateYYYYMM = (date) => {
    const YYYYObj = date.toString().split("-");

    if  (YYYYObj.length == 2 && isNumber(YYYYObj[0]) && isNumber(YYYYObj[1])){
        if (YYYYObj[0] > 1500 && YYYYObj[0] <= 9999 && YYYYObj[1] > 0 && YYYYObj[1] < 13 ){
            return date;
        }
    }

    const fullDate = new Date();
    fullDate.setDate(1); // Set the day to 1st of the month
    fullDate.setHours(12, 0, 0, 0); // Set the time to 12:00:00
    const yearMonth = `${fullDate.getFullYear()}-${String(fullDate.getMonth()+1).padStart(2, '0')}`;
    return yearMonth;
}

function isNumber(n) {
    return !isNaN(parseFloat(n)) && isFinite(n);
}

const convertImageToPdf = async (file) => {
    let imgBytes;
    let imgType = file.type;
  
    // Se não for JPG/PNG, converte para PNG primeiro
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      file = await convertToPng(file);
      imgType = "image/png"; // Atualiza o tipo após conversão
    }
  
    imgBytes = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.create();
  
    let img;
    if (imgType === "image/png") {
      img = await pdfDoc.embedPng(imgBytes);
    } else {
      img = await pdfDoc.embedJpg(imgBytes);
    }
  
    const page = pdfDoc.addPage([img.width, img.height]);
    page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
  
    return await pdfDoc.save();
};
  
  // Função para converter imagens não suportadas para PNG usando Canvas
const convertToPng = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".png"), { type: "image/png" }));
      }, "image/png");
    };
    img.onerror = (error) => reject(error);
  });
};

const uploadFile = async (doc) => {
  try {
    let fileToUpload;
    let fileName = "documento.pdf"; // Nome padrão, pode ser sobrescrito abaixo

    // Se for base64 puro (string)
    if (typeof doc === "string") {
      const base64Data = doc.replace(/^data:application\/pdf;base64,/, "");

      fileToUpload = new Blob(
        [Uint8Array.from(atob(base64Data), c => c.charCodeAt(0))],
        { type: "application/pdf" }
      );
    }

    // Se for objeto com base64 e nome (opcional)
    else if (doc.base64) {
      const base64Data = doc.base64.replace(/^data:application\/pdf;base64,/, "");
      fileName = doc.name || "documento.pdf";

      fileToUpload = new Blob(
        [Uint8Array.from(atob(base64Data), c => c.charCodeAt(0))],
        { type: "application/pdf" }
      );
    }

    // Se for objeto File
    else if (doc.file instanceof File) {
      fileToUpload = doc.file;
      fileName = doc.file.name;
    } else {
      throw new Error("Formato de entrada inválido. Esperado base64 ou File.");
    }

    // 1. Solicita a Signed URL do backend
    const getUrlResponse = await expensesService.getUploadAuthorizationAWS(fileName, "pdf");
    const { s3url, fileNameUuid } = getUrlResponse.data;

    // 2. Envia para o S3
    await expensesService.sendDocToAWS(s3url, fileToUpload);

    return fileNameUuid;
  } catch (error) {
    console.error("Erro no upload:", error);
    throw error;
  }
};


  // Junta múltiplos PDFs em um só
  const mergePDFs = async (pdfSources) => {
    const mergedPdf = await PDFDocument.create();
  
    for (let i = 0; i < pdfSources.length; i++) {
      const item = pdfSources[i];
      const fileName = item.name || `arquivo-${i + 1}`;

      console.log(`🔄 Processando ${fileName} (${i + 1}/${pdfSources.length})`);
  
      try {
        let fileBytes;
  
        // Verifica se é um File ou um buffer
        if (item instanceof File) {
          fileBytes = await item.arrayBuffer();
        } else if (item.buffer && item.name) {
          fileBytes = item.buffer;
        } else {
          console.warn(`⚠️ Tipo de entrada inválido para ${fileName}`);
          continue;
        }
  
        const pdf = await PDFDocument.load(fileBytes, { ignoreEncryption: true });
        const totalPages = pdf.getPageCount();
  
        if (totalPages === 0) {
          console.warn(`⚠️ Arquivo ${fileName} não tem páginas`);
          continue;
        }
  
        const copiedPages = await mergedPdf.copyPages(pdf, [...Array(totalPages).keys()]);
        copiedPages.forEach((page, idx) => {
          mergedPdf.addPage(page);
          //console.log(`📄 Página ${idx + 1} adicionada de ${fileName}`);
        });
  
      } catch (err) {
        console.error(`❌ Erro ao processar ${fileName}:`, err);
      }
    }
  
    const mergedPdfBytes = await mergedPdf.save();
  //console.log("✅ Merge finalizado. Total de páginas:", mergedPdf.getPageCount());



  /*const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
  const downloadLink = document.createElement("a");
  downloadLink.href = URL.createObjectURL(blob);
  downloadLink.download = "documento-final.pdf";
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);*/


  return new File([mergedPdfBytes], "documento-final.pdf", { type: "application/pdf" });
};

// Passo 1: Baixar PDF existente
const downloadExistingPdf = async (url) => {
  try {
    const responseLink = await expensesService.getAccessAuthorizationAWS(url);
    const response = await fetch(responseLink.data);
    
    if (!response.ok) {
      throw new Error(`Erro ao baixar o PDF. Status: ${response.status}`);
    }

    const buffer = await response.arrayBuffer();
    return buffer;
  } catch (err) {
    console.error('❌ Falha ao baixar o PDF da S3:', err);
    throw err; // isso para o processo onde essa função for chamada
  }
};

export default { usePrevious, currencyFormat, isValidCNPJ, isValidCNPJDigits, isValidCPF, isValidCnpjCpf, isValidDate, returnDateYYYYMM, convertImageToPdf, uploadFile, mergePDFs, downloadExistingPdf};